import React from 'react';
import type { Split } from '../../lib/market';
import { n } from '../../lib/market';

interface StatTileProps {
  label:   string;
  value:   React.ReactNode;
  sub?:    React.ReactNode;
  /** UK/US breakdown shown under the value. */
  split?:  Split | null;
  splitFmt?: (v: number) => string;
  /** Percentage change, shown next to the value. */
  delta?:  number | null;
  /** Where the number comes from, in plain words. */
  source?: string;
  tone?:   'default' | 'warn' | 'bad' | 'good';
  /** Kept for older call sites; ignored by the new look. */
  accent?: string;
  icon?:   unknown;
}

const VALUE_TONE: Record<NonNullable<StatTileProps['tone']>, string> = {
  default: 'text-stone-900',
  warn: 'text-amber-700',
  bad: 'text-rose-700',
  good: 'text-emerald-700',
};

/** One KPI. Sentence-case label, tabular numerals, UK/US line, source note. */
export const StatTile: React.FC<StatTileProps> = ({ label, value, sub, split, splitFmt, delta, source, tone = 'default' }) => {
  const f = splitFmt ?? ((v: number) => v.toLocaleString('en-GB'));
  return (
    <div className="bg-white rounded-lg border border-stone-200 px-4 py-3.5 flex flex-col min-w-0">
      <span className="text-[12.5px] font-medium text-stone-500">{label}</span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-1.5">
        <span className={`text-[22px] sm:text-[25px] leading-none font-semibold tracking-[-0.02em] tabular-nums ${VALUE_TONE[tone]}`}>{value}</span>
        {delta != null && (
          <span className={`text-[12px] font-medium tabular-nums ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-rose-600' : 'text-stone-400'}`}>
            {delta > 0 ? '+' : ''}{delta}%
          </span>
        )}
      </div>
      {split && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-[12px] text-stone-600 tabular-nums">
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-sm bg-uk" />UK {f(n(split.uk))}</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-sm bg-us" />US {f(n(split.us))}</span>
        </div>
      )}
      {sub && <p className="text-[12px] text-stone-500 mt-1.5">{sub}</p>}
      {source && <p className="text-[11px] text-stone-400 mt-auto pt-2 leading-snug">{source}</p>}
    </div>
  );
};
